/**
 * ScannerBridge.ts
 *
 * This module facilitates communication between the Vue3 application and the
 * FS20 Scanner Bridge (VB.NET application wrapper).
 */

export interface ScannerData {
    type: 'data'
    payload: string
}

export interface ScannerStatus {
    type: 'status'
    status: 'Connected' | 'Disconnected' | 'Error'
    detail: string
}

export type ScannerMessage = ScannerData | ScannerStatus;

type ScanCallback = (data: string) => void;
type StatusCallback = (status: ScannerStatus) => void;

interface WebView2 {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    addEventListener(type: string, listener: (event: any) => void): void
    postMessage(message: any): void
    /* eslint-enable @typescript-eslint/no-explicit-any */
}

interface Chrome {
    webview: WebView2
}

interface ScannerWindow extends Window {
    chrome: Chrome
}

class ScannerBridge {
    private scanCallbacks: ScanCallback[] = [];
    private statusCallbacks: StatusCallback[] = [];
    private isInitialized = false;

    constructor() {
        // Auto-initialize if the environment is already available
        this.init()
    }

    /**
     * Explicitly initialize the scanner bridge.
     * Safe to call multiple times.
     */
    public init(retries = 10) {
        if (this.isInitialized) return

        const win = window as unknown as ScannerWindow
        if (typeof window !== 'undefined' && win.chrome?.webview) {
            console.log('Scanner Bridge: WebView2 environment detected.')
            win.chrome.webview.addEventListener('message', (event: MessageEvent) => {
                this.handleMessage(event.data)
            })
            this.isInitialized = true
        } else if (typeof window !== 'undefined' && retries > 0) {
            // Retry after a short delay to allow WebView2 script injection
            setTimeout(() => this.init(retries - 1), 200)
        } else if (typeof window !== 'undefined') {
            console.warn('Scanner Bridge: WebView2 environment not detected after multiple attempts.')
        }
    }

    private handleMessage(message: unknown) {
        // WebView2 might send data as a string or object depending on how PostWebMessageAsJson is handled
        const data: ScannerMessage = typeof message === 'string' ? JSON.parse(message) : message

        if (data.type === 'data') {
            this.scanCallbacks.forEach(cb => cb(data.payload))
        } else if (data.type === 'status') {
            this.statusCallbacks.forEach(cb => cb(data))
        }
    }

    /**
     * Register a callback for when data is scanned.
     * @returns Unsubscribe function
     */
    public onScan(callback: ScanCallback): () => void {
        this.scanCallbacks.push(callback)
        return () => this.offScan(callback)
    }

    /**
     * Unregister a scan callback.
     */
    public offScan(callback: ScanCallback) {
        this.scanCallbacks = this.scanCallbacks.filter(cb => cb !== callback)
    }

    /**
     * Register a callback for scanner status changes.
     * @returns Unsubscribe function
     */
    public onStatusChange(callback: StatusCallback): () => void {
        this.statusCallbacks.push(callback)
        return () => this.offStatusChange(callback)
    }

    /**
     * Unregister a status change callback.
     */
    public offStatusChange(callback: StatusCallback) {
        this.statusCallbacks = this.statusCallbacks.filter(cb => cb !== callback)
    }

    /**
     * Command the VB app to reconnect to the scanner.
     */
    public reconnect() {
        this.sendCommand('reconnect')
    }

    /**
     * Command the VB app to close.
     */
    public closeApp() {
        this.sendCommand('close')
    }

    private sendCommand(command: string) {
        const win = window as unknown as ScannerWindow
        if (win.chrome?.webview) {
            console.log(`sending command ${command}`)
            win.chrome.webview.postMessage(command)
        } else {
            console.warn('Scanner Bridge: WebView2 environment not detected.')
        }
    }
}

export const scannerBridge = new ScannerBridge()
